/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getEnvVarOptional, COSMOS_SITE_BASE_PATH } from "@cosmos/common-utils";

export interface LocalizedUrlResult {
	/** Canonical URL: clean for the default language, ?lang= for other languages. */
	fullUrl: string;
	/** URL without a language query parameter, used for og:url and breadcrumbs. */
	plainUrl: string;
}

export interface LocalizedUrlOptions {
	/** Public tenant origin override for a multi-tenant application. */
	serverUrl?: string;
	/** Public tenant mount path override for a multi-tenant application. */
	basePath?: string;
}

/** Builds public URLs without locale path prefixes, including when the origin is unset. */
export const buildLocalizedUrl = (
	locale: string,
	slug: string,
	defaultLocale?: string,
	options?: LocalizedUrlOptions,
): LocalizedUrlResult => {
	const normalizedSlug = slug === "/home" ? "/" : slug || "/";
	const serverUrl = options?.serverUrl ?? getEnvVarOptional("EPIC_SERVER_URL") ?? "";
	const basePath = options?.basePath ?? COSMOS_SITE_BASE_PATH();
	const plainUrl = `${serverUrl}${basePath}${normalizedSlug}`;
	if (locale === defaultLocale) return { fullUrl: plainUrl, plainUrl };

	const url = new URL(plainUrl, "https://cosmos.invalid");
	url.searchParams.set("lang", locale);
	const fullUrl = /^https?:\/\//.test(plainUrl) ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
	return { fullUrl, plainUrl };
};
